import { X, Pencil, Package, ArchiveRestore, Archive, Trash2, Calendar, Gauge, DollarSign, Tag, MapPin, Circle } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import useConfirm from "@/lib/useConfirm";
import { componentTypeMeta, COMPONENT_STATUS_META, TONE_COLORS, usageBreakdown } from "@/lib/componentTypes";

/**
 * Component detail sheet — opens from a row tap on both mobile & desktop.
 * Single scroll layout for both breakpoints (design converged after
 * comparing the mobile-tabs and desktop-linear mocks; a tabbed mobile
 * with a linear desktop split the codebase for very little gain).
 * On mobile the Sheet slides up from the bottom; on md+ it slides in
 * from the right (shadcn Sheet default) — this matches VehicleDetailSheet.
 */
export default function ComponentDetailSheet({
  open, onOpenChange, component, vehicle, currencySymbol = "$",
  onEdit, onDelete, onStatusChange,
}) {
  const { confirm, ConfirmDialog } = useConfirm();
  if (!component) return null;

  const typeMeta = componentTypeMeta(component.type);
  const Icon = typeMeta.icon;
  const status = COMPONENT_STATUS_META[component.status] || COMPONENT_STATUS_META.active;
  const wearSum = typeMeta.summarizeWear(component.wear || {});
  const tone = typeMeta.healthTone(component.wear || {});
  const toneColor = TONE_COLORS[tone];
  const usage = usageBreakdown(component, { distanceDrivenKm: component.distance_driven_km });
  const positionLabel = typeMeta.positions?.find((p) => p.value === component.position)?.label || "";
  const title = [component.brand, component.model].filter(Boolean).join(" ") || typeMeta.label;

  const history = (component.history || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const confirmArchive = async () => {
    const ok = await confirm({
      title: "Archive this component?",
      description: "It won't appear in the active list anymore. You can restore it from the Archived filter.",
      confirmLabel: "Archive",
    });
    if (ok) onStatusChange?.("archived");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[500px] bg-[var(--bg-canvas)] border-l border-[var(--border-hover)] text-[var(--text-primary)] p-0 overflow-y-auto flex flex-col"
        data-testid="component-detail-sheet"
      >
        {/* ---------- Hero ---------- */}
        <div className="relative flex items-start gap-4 p-5 border-b border-[var(--border-hover)]">
          <div
            className="w-14 h-14 shrink-0 flex items-center justify-center bg-[var(--bg-elevated)] border border-[var(--border-hover)]"
            style={{ borderRadius: 999 }}
          >
            <Icon className="w-6 h-6 text-[var(--text-primary)]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0 pr-8">
            <div className="font-display text-lg tracking-tight text-[var(--text-primary)] truncate">{title}</div>
            <div className="text-xs text-[var(--text-muted)] mt-0.5">
              {typeMeta.label}{positionLabel ? ` · ${positionLabel}` : ""}
            </div>
            <span
              className="mt-2 inline-flex text-[10px] tracking-wider uppercase px-1.5 py-0.5 font-mono-data"
              style={{ color: status.color, background: status.ring, borderRadius: 2 }}
            >
              {status.label}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 tap w-8 h-8 flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            aria-label="Close"
            data-testid="component-detail-close"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* ---------- Overview ---------- */}
        <Section label="Overview">
          <Row icon={Tag} label="Component Type" value={typeMeta.label} />
          {vehicle && <Row icon={Circle} label="Vehicle" value={vehicle.name} />}
          {positionLabel && <Row icon={MapPin} label="Position" value={positionLabel} />}
          <Row icon={Calendar} label="Installation Date" value={component.install_date || "—"} />
          <Row icon={Gauge} label="Installation Odometer" value={component.install_odometer_km != null ? `${Math.round(component.install_odometer_km).toLocaleString()} km` : "—"} />
          <Row icon={Calendar} label="Purchase Date" value={component.purchase_date || "—"} />
          <Row icon={DollarSign} label="Purchase Price" value={component.purchase_price != null ? `${currencySymbol}${Number(component.purchase_price).toFixed(2)}` : "—"} />
          <Row icon={Tag} label="Brand / Model" value={[component.brand, component.model].filter(Boolean).join(" ") || "—"} />
        </Section>

        {/* ---------- Wear & Usage ---------- */}
        <Section label="Wear & Usage">
          <div className="px-5 pb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm text-[var(--text-primary)]">{typeMeta.wearLabel || "Wear"}</span>
              <span className="font-mono-data text-sm text-[var(--text-primary)]">
                {wearSum.display}
                {wearSum.secondary && <span className="text-[var(--text-muted)] ml-1">{wearSum.secondary}</span>}
              </span>
            </div>
            {typeof wearSum.pct === "number" && (
              <div className="h-1 bg-[var(--bg-elevated)] overflow-hidden" style={{ borderRadius: 2 }}>
                <div className="h-full" style={{ width: `${wearSum.pct}%`, background: toneColor }} />
              </div>
            )}
          </div>
          {/* Type-specific fields verbatim from the registry */}
          {typeMeta.wearFields?.map((f) => {
            const raw = component.wear?.[f.key];
            const display = raw == null || raw === ""
              ? "—"
              : f.type === "select"
                ? (f.options?.find((o) => o.value === raw)?.label ?? String(raw))
                : `${raw}${labelUnit(f.label)}`;
            return <Row key={f.key} label={f.label.replace(/\s*\([^)]*\)\s*$/, "")} value={display} />;
          })}
          <Row label="Distance Driven" value={typeof component.distance_driven_km === "number" ? `${Math.round(component.distance_driven_km).toLocaleString()} km` : "—"} />
          <Row label="Est. Remaining" value={usage.remaining} />
        </Section>

        {/* ---------- History ---------- */}
        {history.length > 0 && (
          <Section label="History">
            <div className="px-5 pb-3">
              <div className="relative pl-4">
                <div className="absolute left-[3px] top-1 bottom-1 w-px bg-[var(--border-hover)]" />
                {history.map((h) => (
                  <div key={h.id || `${h.date}-${h.created_at}`} className="relative pb-3 last:pb-0">
                    <div className="absolute -left-[13px] top-1.5 w-2 h-2 rounded-full" style={{ background: h.event === "created" || h.event === "status_active" ? "#34D17A" : "#8A8A8E" }} />
                    <div className="text-sm text-[var(--text-primary)]">{prettyEvent(h.event)}</div>
                    <div className="text-[11px] text-[var(--text-muted)] font-mono-data">
                      {h.date || ""}{h.odometer_km != null ? ` · ${Math.round(h.odometer_km).toLocaleString()} km` : ""}
                    </div>
                    {h.notes && <div className="text-xs text-[var(--text-secondary)] mt-0.5">{h.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ---------- Notes ---------- */}
        {component.notes && (
          <Section label="Notes">
            <div className="px-5 pb-3 text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
              {component.notes}
            </div>
          </Section>
        )}

        {/* ---------- Actions ---------- */}
        <div className="mt-auto p-4 border-t border-[var(--border-hover)] flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onEdit}
            className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] flex-1"
            style={{ borderRadius: 2 }}
            data-testid="component-detail-edit"
          >
            <Pencil className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
            Edit
          </Button>
          {component.status === "active" && (
            <Button
              variant="outline"
              onClick={() => onStatusChange?.("stored")}
              className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] flex-1"
              style={{ borderRadius: 2 }}
              data-testid="component-detail-store"
            >
              <Package className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Move to Storage
            </Button>
          )}
          {component.status === "stored" && (
            <Button
              variant="outline"
              onClick={() => onStatusChange?.("active")}
              className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] flex-1"
              style={{ borderRadius: 2 }}
              data-testid="component-detail-install"
            >
              <ArchiveRestore className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Install
            </Button>
          )}
          {component.status === "archived" ? (
            <Button
              onClick={onDelete}
              className="bg-[#E82127] text-white hover:bg-[#FF2A30] flex-1"
              style={{ borderRadius: 2 }}
              data-testid="component-detail-delete"
            >
              <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Delete
            </Button>
          ) : (
            <Button
              onClick={confirmArchive}
              className="bg-[#E82127] text-white hover:bg-[#FF2A30] flex-1"
              style={{ borderRadius: 2 }}
              data-testid="component-detail-archive"
            >
              <Archive className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Archive
            </Button>
          )}
        </div>
      </SheetContent>
      <ConfirmDialog />
    </Sheet>
  );
}

function Section({ label, children }) {
  return (
    <div className="border-b border-[var(--border-hover)] py-3">
      <div className="label-tiny px-5 mb-2">{label}</div>
      <div className="space-y-0">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-1.5">
      <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
        {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
        <span>{label}</span>
      </div>
      <div className="font-mono-data text-sm text-[var(--text-primary)] text-right">{value}</div>
    </div>
  );
}

function labelUnit(label) {
  // "Current Thickness (mm)" → " mm". Cheap trailing-unit extraction so the
  // read-only display in the detail sheet mirrors the input field's unit.
  const m = /\(([^)]+)\)/.exec(label);
  return m ? ` ${m[1]}` : "";
}

function prettyEvent(e) {
  const map = { created: "Installed", status_active: "Installed / Restored", status_stored: "Moved to Storage", status_archived: "Archived" };
  return map[e] || e;
}
